import React, { useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import CopyButtonCDS from '@sharedCR/coreDesignSystem/Buttons/CopyButtonCDS';
import DataDisplay from '@sharedCR/coreDesignSystem/DataDisplay';
import { OpenIcon } from '@sharedCR/coreDesignSystem/icons';
import useCopyWithNotice from 'app/shared/modules/EntityAuditHistory/hooks/useCopyWithNotice';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  CHECKSUM_COPIED_MESSAGE,
  FILE_NOT_AVAILABLE_LABEL,
  FileFactLabel,
  NO_FILE_LABEL,
  OPEN_FILE_LABEL,
} from '../../FormChangeHistory.constants';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import { formatChecksum, getFileFacts } from '../../FormChangeHistory.utils';

import { checksumRowStyles, factsStyles, fileSideStyles } from './TemplateFileSide.styles';

/**
 * One side's stored file: its facts, the checksum to copy, and the action that opens it when there
 * is one (`onOpen`).
 */
const TemplateFileSide = ({
  side,
  title,
  copyChecksumLabel,
  facts = null,
  isPdf = false,
  onOpen = null,
}) => {
  const copyWithNotice = useCopyWithNotice();

  const onCopyChecksum = useCallback(
    () => copyWithNotice(facts.sha256, CHECKSUM_COPIED_MESSAGE),
    [copyWithNotice, facts],
  );

  const onOpenClick = useCallback(() => onOpen(side), [onOpen, side]);

  return (
    <Box data-side={side} data-testid={FormChangeHistoryTestIds.FILE_SIDE} sx={fileSideStyles}>
      <Typography color="text.secondary" variant="medium12">
        {title}
      </Typography>

      {!facts && <Typography variant="regular14">{NO_FILE_LABEL}</Typography>}

      {!!facts && !facts.available && (
        <Typography color="text.secondary" variant="regular14">
          {FILE_NOT_AVAILABLE_LABEL}
        </Typography>
      )}

      {!!facts && (
        <Box sx={factsStyles}>
          {getFileFacts(facts, isPdf).map(fact => (
            <DataDisplay key={fact.title} title={fact.title} value={fact.value} />
          ))}
          {!!facts.sha256 && (
            <Box sx={checksumRowStyles}>
              <DataDisplay title={FileFactLabel.CHECKSUM} value={formatChecksum(facts.sha256)} />
              <Box data-testid={FormChangeHistoryTestIds.CHECKSUM_COPY}>
                <CopyButtonCDS label={copyChecksumLabel} onClick={onCopyChecksum} />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {!!facts?.available && !!onOpen && (
        <Box>
          <Button
            data-side={side}
            data-testid={FormChangeHistoryTestIds.OPEN_FILE_BUTTON}
            startIcon={<OpenIcon size={IconSize[16]} />}
            variant="line"
            onClick={onOpenClick}
          >
            {OPEN_FILE_LABEL[side]}
          </Button>
        </Box>
      )}
    </Box>
  );
};

TemplateFileSide.propTypes = {
  side: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  copyChecksumLabel: PropTypes.string.isRequired,
  facts: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    sizeBytes: PropTypes.number,
    sha256: PropTypes.string,
    pages: PropTypes.number,
    savedAt: PropTypes.string,
  }),
  isPdf: PropTypes.bool,
  onOpen: PropTypes.func,
};

export default TemplateFileSide;
